import React, { useState, useEffect, useCallback } from "react";
import {
    Server,
    RefreshCw,
    Plus,
    Pencil,
    Trash2,
    Link2,
    Eye,
    AlertCircle,
    CheckCircle,
    Shield,
    Users,
} from "lucide-react";
import * as api from "../../lib/api";
import { useApp } from "../../context/AppContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "../ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { EmptyState } from "../shared/EmptyState";
import { PageLoader } from "../shared/LoadingSpinner";
import { toast } from "sonner";

export function AdminLdap() {
    const { departments, users } = useApp();
    const [loading, setLoading] = useState(true);
    const [ldapConfig, setLdapConfig] = useState(null);
    const [mappings, setMappings] = useState([]);
    const [exclusions, setExclusions] = useState([]);
    const [syncPreview, setSyncPreview] = useState([]);
    const [showPreview, setShowPreview] = useState(false);

    // Config form
    const [configForm, setConfigForm] = useState({
        server_url: "",
        bind_dn: "",
        bind_password: "",
        base_dn: "",
        user_search_base: "",
        user_search_filter: "(objectClass=user)",
        group_search_base: "",
        group_search_filter: "(objectClass=group)",
        sync_enabled: false,
        sync_interval_minutes: 60,
    });

    // Mapping dialog
    const [mappingDialogOpen, setMappingDialogOpen] = useState(false);
    const [editingMapping, setEditingMapping] = useState(null);
    const [mappingForm, setMappingForm] = useState({
        security_group_dn: "",
        security_group_name: "",
        department_id: "",
        priority: 0,
        is_active: true,
    });

    // Exclusion dialog
    const [exclusionDialogOpen, setExclusionDialogOpen] = useState(false);
    const [exclusionForm, setExclusionForm] = useState({
        user_id: "",
        security_group_dn: "",
        reason: "",
    });

    useEffect(() => {
        loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            const [configData, mappingsData, exclusionsData] = await Promise.all([
                api.getLdapConfig(),
                api.getSecurityGroupMappings(),
                api.getUserGroupExclusions(),
            ]);
            
            if (configData && Object.keys(configData).length > 0) {
                setLdapConfig(configData);
                setConfigForm({
                    ...configForm,
                    ...configData,
                });
            }
            setMappings(mappingsData);
            setExclusions(exclusionsData);
        } catch (error) {
            console.error("Failed to load LDAP data:", error);
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSaveConfig = async () => {
        try {
            await api.saveLdapConfig(configForm);
            toast.success("Configuration LDAP sauvegardée");
            loadData();
        } catch (error) {
            toast.error("Erreur lors de la sauvegarde");
        }
    };

    const handleTestConnection = async () => {
        try {
            const result = await api.testLdapConnection();
            if (result.success) {
                toast.success(result.message);
            } else {
                toast.error(result.message || "Échec de la connexion");
            }
        } catch (error) {
            toast.error("Erreur lors du test de connexion");
        }
    };

    const handleSync = async () => {
        try {
            const result = await api.triggerLdapSync();
            if (result.success) {
                toast.success(result.message);
                loadData();
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || "Erreur lors de la synchronisation");
        }
    };

    const handlePreviewSync = async () => {
        try {
            const preview = await api.previewLdapSync();
            setSyncPreview(preview);
            setShowPreview(true);
        } catch (error) {
            toast.error("Erreur lors de la prévisualisation");
        }
    };

    // Mapping handlers
    const openMappingDialog = (mapping = null) => {
        if (mapping) {
            setEditingMapping(mapping);
            setMappingForm({
                security_group_dn: mapping.security_group_dn,
                security_group_name: mapping.security_group_name,
                department_id: mapping.department_id,
                priority: mapping.priority,
                is_active: mapping.is_active,
            });
        } else {
            setEditingMapping(null);
            setMappingForm({
                security_group_dn: "",
                security_group_name: "",
                department_id: "",
                priority: 0,
                is_active: true,
            });
        }
        setMappingDialogOpen(true);
    };

    const handleSaveMapping = async () => {
        try {
            if (editingMapping) {
                await api.updateSecurityGroupMapping(editingMapping.id, mappingForm);
                toast.success("Mapping mis à jour");
            } else {
                await api.createSecurityGroupMapping(mappingForm);
                toast.success("Mapping créé");
            }
            setMappingDialogOpen(false);
            loadData();
        } catch (error) {
            toast.error("Erreur lors de la sauvegarde");
        }
    };

    const handleDeleteMapping = async (id) => {
        if (!window.confirm("Supprimer ce mapping ?")) return;
        try {
            await api.deleteSecurityGroupMapping(id);
            toast.success("Mapping supprimé");
            loadData();
        } catch (error) {
            toast.error("Erreur lors de la suppression");
        }
    };

    // Exclusion handlers
    const openExclusionDialog = () => {
        setExclusionForm({ user_id: "", security_group_dn: "", reason: "" });
        setExclusionDialogOpen(true);
    };

    const handleSaveExclusion = async () => {
        try {
            await api.createUserGroupExclusion(exclusionForm);
            toast.success("Exclusion créée");
            setExclusionDialogOpen(false);
            loadData();
        } catch (error) {
            toast.error("Erreur lors de la création");
        }
    };

    const handleDeleteExclusion = async (id) => {
        if (!window.confirm("Supprimer cette exclusion ?")) return;
        try {
            await api.deleteUserGroupExclusion(id);
            toast.success("Exclusion supprimée");
            loadData();
        } catch (error) {
            toast.error("Erreur lors de la suppression");
        }
    };

    const getDepartmentName = (deptId) => {
        return departments.find((d) => d.id === deptId)?.name || deptId;
    };

    const getUserName = (userId) => {
        const user = users.find((u) => u.id === userId);
        return user ? `${user.first_name} ${user.last_name}` : userId;
    };

    if (loading) return <PageLoader />;

    return (
        <div className="page-container" data-testid="admin-ldap">
            <div className="page-header">
                <h1 className="page-title">Configuration LDAP / Active Directory</h1>
                <p className="page-subtitle">
                    Synchronisation des utilisateurs et mapping des security groups
                </p>
            </div>

            <Tabs defaultValue="config">
                <TabsList className="mb-6">
                    <TabsTrigger value="config" className="gap-2">
                        <Server className="w-4 h-4" />
                        Configuration
                    </TabsTrigger>
                    <TabsTrigger value="mappings" className="gap-2">
                        <Link2 className="w-4 h-4" />
                        Mappings ({mappings.length})
                    </TabsTrigger>
                    <TabsTrigger value="exclusions" className="gap-2">
                        <Shield className="w-4 h-4" />
                        Exclusions ({exclusions.length})
                    </TabsTrigger>
                    <TabsTrigger value="sync" className="gap-2">
                        <RefreshCw className="w-4 h-4" />
                        Synchronisation
                    </TabsTrigger>
                </TabsList>

                {/* Configuration Tab */}
                <TabsContent value="config">
                    <div className="card">
                        <div className="card-header">
                            <h2 className="text-sm font-semibold">Connexion au serveur LDAP</h2>
                        </div>
                        <div className="card-body space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <Label className="form-label">URL du serveur *</Label>
                                    <Input
                                        value={configForm.server_url}
                                        onChange={(e) => setConfigForm({ ...configForm, server_url: e.target.value })}
                                        placeholder="ldap://dc.entreprise.local ou ldaps://dc.entreprise.local:636"
                                        data-testid="ldap-server-url"
                                    />
                                </div>
                                <div>
                                    <Label className="form-label">Base DN *</Label>
                                    <Input
                                        value={configForm.base_dn}
                                        onChange={(e) => setConfigForm({ ...configForm, base_dn: e.target.value })}
                                        placeholder="DC=entreprise,DC=local"
                                        data-testid="ldap-base-dn"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <Label className="form-label">Bind DN (compte de service)</Label>
                                    <Input
                                        value={configForm.bind_dn}
                                        onChange={(e) => setConfigForm({ ...configForm, bind_dn: e.target.value })}
                                        placeholder="CN=svc_ticketing,OU=ServiceAccounts,DC=entreprise,DC=local"
                                        data-testid="ldap-bind-dn"
                                    />
                                </div>
                                <div>
                                    <Label className="form-label">Mot de passe Bind</Label>
                                    <Input
                                        type="password"
                                        value={configForm.bind_password}
                                        onChange={(e) => setConfigForm({ ...configForm, bind_password: e.target.value })}
                                        placeholder="••••••••"
                                        data-testid="ldap-bind-password"
                                    />
                                </div>
                            </div>

                            <div className="border-t pt-4">
                                <h3 className="text-sm font-medium mb-4">Recherche utilisateurs</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <Label className="form-label">Base de recherche utilisateurs</Label>
                                        <Input
                                            value={configForm.user_search_base}
                                            onChange={(e) => setConfigForm({ ...configForm, user_search_base: e.target.value })}
                                            placeholder="OU=Users,DC=entreprise,DC=local"
                                        />
                                    </div>
                                    <div>
                                        <Label className="form-label">Filtre utilisateurs</Label>
                                        <Input
                                            value={configForm.user_search_filter}
                                            onChange={(e) => setConfigForm({ ...configForm, user_search_filter: e.target.value })}
                                            placeholder="(objectClass=user)"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="border-t pt-4">
                                <h3 className="text-sm font-medium mb-4">Recherche groupes</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <Label className="form-label">Base de recherche groupes</Label>
                                        <Input
                                            value={configForm.group_search_base}
                                            onChange={(e) => setConfigForm({ ...configForm, group_search_base: e.target.value })}
                                            placeholder="OU=Groups,DC=entreprise,DC=local"
                                        />
                                    </div>
                                    <div>
                                        <Label className="form-label">Filtre groupes</Label>
                                        <Input
                                            value={configForm.group_search_filter}
                                            onChange={(e) => setConfigForm({ ...configForm, group_search_filter: e.target.value })}
                                            placeholder="(objectClass=group)"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="border-t pt-4">
                                <h3 className="text-sm font-medium mb-4">Synchronisation automatique</h3>
                                <div className="flex items-center gap-4">
                                    <label className="flex items-center gap-2">
                                        <Checkbox
                                            checked={configForm.sync_enabled}
                                            onCheckedChange={(checked) => setConfigForm({ ...configForm, sync_enabled: checked })}
                                            data-testid="ldap-sync-enabled"
                                        />
                                        <span className="text-sm">Activer la synchronisation automatique</span>
                                    </label>
                                    {configForm.sync_enabled && (
                                        <div className="flex items-center gap-2">
                                            <Label className="text-sm">Intervalle (minutes):</Label>
                                            <Input
                                                type="number"
                                                min={5}
                                                className="w-24"
                                                value={configForm.sync_interval_minutes}
                                                onChange={(e) => setConfigForm({ ...configForm, sync_interval_minutes: parseInt(e.target.value) || 60 })}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="flex items-center justify-between pt-4 border-t">
                                <Button variant="outline" onClick={handleTestConnection} data-testid="test-ldap-btn">
                                    <CheckCircle className="w-4 h-4 mr-2" />
                                    Tester la connexion
                                </Button>
                                <Button onClick={handleSaveConfig} data-testid="save-ldap-config-btn">
                                    Enregistrer la configuration
                                </Button>
                            </div>

                            {ldapConfig?.last_sync_at && (
                                <div className="text-sm text-gray-500 pt-2">
                                    Dernière synchronisation: {new Date(ldapConfig.last_sync_at).toLocaleString("fr-FR")}
                                    {ldapConfig.last_sync_status && (
                                        <span className={`ml-2 ${ldapConfig.last_sync_status === "success" ? "text-green-600" : "text-orange-600"}`}>
                                            ({ldapConfig.last_sync_status})
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </TabsContent>

                {/* Mappings Tab */}
                <TabsContent value="mappings">
                    <div className="card">
                        <div className="card-header flex items-center justify-between">
                            <div>
                                <h2 className="text-sm font-semibold">Mapping Security Groups → Départements</h2>
                                <p className="text-xs text-gray-500 mt-1">
                                    Associez les security groups AD aux départements. La priorité détermine quel département est assigné si un utilisateur est dans plusieurs groupes.
                                </p>
                            </div>
                            <Button onClick={() => openMappingDialog()} data-testid="add-mapping-btn">
                                <Plus className="w-4 h-4 mr-2" />
                                Ajouter
                            </Button>
                        </div>
                        <div className="card-body p-0">
                            {mappings.length === 0 ? (
                                <EmptyState
                                    icon={Link2}
                                    title="Aucun mapping"
                                    description="Ajoutez des mappings entre security groups et départements"
                                />
                            ) : (
                                <div className="table-container">
                                    <table className="data-table">
                                        <thead>
                                            <tr>
                                                <th>Security Group</th>
                                                <th>Département</th>
                                                <th>Priorité</th>
                                                <th>Actif</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {mappings.map((m) => (
                                                <tr key={m.id} data-testid={`mapping-row-${m.id}`}>
                                                    <td>
                                                        <div>
                                                            <div className="font-medium">{m.security_group_name}</div>
                                                            <div className="text-xs text-gray-400 font-mono truncate max-w-xs">
                                                                {m.security_group_dn}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>{getDepartmentName(m.department_id)}</td>
                                                    <td>
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded bg-blue-50 text-blue-700 text-xs font-medium">
                                                            {m.priority}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {m.is_active ? (
                                                            <span className="text-green-600">Oui</span>
                                                        ) : (
                                                            <span className="text-gray-400">Non</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <div className="flex items-center gap-2 justify-end">
                                                            <Button variant="ghost" size="sm" onClick={() => openMappingDialog(m)}>
                                                                <Pencil className="w-4 h-4" />
                                                            </Button>
                                                            <Button variant="ghost" size="sm" onClick={() => handleDeleteMapping(m.id)}>
                                                                <Trash2 className="w-4 h-4 text-red-500" />
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </TabsContent>

                {/* Exclusions Tab */}
                <TabsContent value="exclusions">
                    <div className="card">
                        <div className="card-header flex items-center justify-between">
                            <div>
                                <h2 className="text-sm font-semibold">Règles d'exclusion</h2>
                                <p className="text-xs text-gray-500 mt-1">
                                    Ignorez certains security groups pour des utilisateurs spécifiques (cas de conflits multiples groupes)
                                </p>
                            </div>
                            <Button onClick={openExclusionDialog} data-testid="add-exclusion-btn">
                                <Plus className="w-4 h-4 mr-2" />
                                Ajouter
                            </Button>
                        </div>
                        <div className="card-body p-0">
                            {exclusions.length === 0 ? (
                                <EmptyState
                                    icon={Shield}
                                    title="Aucune exclusion"
                                    description="Ajoutez des règles d'exclusion si nécessaire"
                                />
                            ) : (
                                <div className="table-container">
                                    <table className="data-table">
                                        <thead>
                                            <tr>
                                                <th>Utilisateur</th>
                                                <th>Security Group ignoré</th>
                                                <th>Raison</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {exclusions.map((e) => (
                                                <tr key={e.id} data-testid={`exclusion-row-${e.id}`}>
                                                    <td className="font-medium">{getUserName(e.user_id)}</td>
                                                    <td className="font-mono text-sm">{e.security_group_dn}</td>
                                                    <td className="text-gray-500">{e.reason || "—"}</td>
                                                    <td>
                                                        <Button variant="ghost" size="sm" onClick={() => handleDeleteExclusion(e.id)}>
                                                            <Trash2 className="w-4 h-4 text-red-500" />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </TabsContent>

                {/* Sync Tab */}
                <TabsContent value="sync">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="card">
                            <div className="card-header">
                                <h2 className="text-sm font-semibold">Actions de synchronisation</h2>
                            </div>
                            <div className="card-body space-y-4">
                                <p className="text-sm text-gray-600">
                                    La synchronisation importe les utilisateurs depuis Active Directory et applique
                                    les mappings de security groups pour déterminer automatiquement leur département.
                                </p>
                                <div className="flex flex-col gap-3">
                                    <Button variant="outline" onClick={handlePreviewSync} data-testid="preview-sync-btn">
                                        <Eye className="w-4 h-4 mr-2" />
                                        Prévisualiser les changements
                                    </Button>
                                    <Button onClick={handleSync} data-testid="trigger-sync-btn">
                                        <RefreshCw className="w-4 h-4 mr-2" />
                                        Lancer la synchronisation
                                    </Button>
                                </div>
                            </div>
                        </div>

                        <div className="card">
                            <div className="card-header">
                                <h2 className="text-sm font-semibold">Principe du manager automatique</h2>
                            </div>
                            <div className="card-body">
                                <div className="space-y-3 text-sm text-gray-600">
                                    <p>
                                        <strong>1. Le manager est défini au niveau du département</strong>
                                        <br />
                                        Chaque département a un responsable défini. Tous les membres de ce département héritent automatiquement de ce manager.
                                    </p>
                                    <p>
                                        <strong>2. Les security groups déterminent le département</strong>
                                        <br />
                                        Quand un utilisateur AD est dans un security group mappé, il est automatiquement assigné au département correspondant.
                                    </p>
                                    <p>
                                        <strong>3. La priorité résout les conflits</strong>
                                        <br />
                                        Si un utilisateur est dans plusieurs security groups mappés, le mapping avec la priorité la plus haute gagne.
                                    </p>
                                    <p>
                                        <strong>4. Les exclusions affinent le comportement</strong>
                                        <br />
                                        Pour des cas spécifiques, vous pouvez ignorer certains security groups pour certains utilisateurs.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Sync Preview */}
                    {showPreview && (
                        <div className="card mt-6">
                            <div className="card-header flex items-center justify-between">
                                <h2 className="text-sm font-semibold">Aperçu des changements</h2>
                                <Button variant="ghost" size="sm" onClick={() => setShowPreview(false)}>
                                    Fermer
                                </Button>
                            </div>
                            <div className="card-body p-0">
                                {syncPreview.length === 0 ? (
                                    <div className="p-6 text-center text-gray-500">
                                        Aucun changement de département détecté
                                    </div>
                                ) : (
                                    <div className="table-container">
                                        <table className="data-table">
                                            <thead>
                                                <tr>
                                                    <th>Utilisateur</th>
                                                    <th>Département actuel</th>
                                                    <th>→</th>
                                                    <th>Nouveau département</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {syncPreview.map((item, idx) => (
                                                    <tr key={item.user_email || idx}>
                                                        <td>
                                                            <div>
                                                                <div className="font-medium">{item.user_name}</div>
                                                                <div className="text-xs text-gray-400">{item.user_email}</div>
                                                            </div>
                                                        </td>
                                                        <td>{item.current_department || "—"}</td>
                                                        <td className="text-gray-400">→</td>
                                                        <td className="font-medium text-blue-600">{item.new_department}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </TabsContent>
            </Tabs>

            {/* Mapping Dialog */}
            <Dialog open={mappingDialogOpen} onOpenChange={setMappingDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {editingMapping ? "Modifier le mapping" : "Nouveau mapping"}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div>
                            <Label className="form-label">Nom du Security Group *</Label>
                            <Input
                                value={mappingForm.security_group_name}
                                onChange={(e) => setMappingForm({ ...mappingForm, security_group_name: e.target.value })}
                                placeholder="Ex: IT-Support"
                                data-testid="mapping-group-name"
                            />
                        </div>
                        <div>
                            <Label className="form-label">DN du Security Group *</Label>
                            <Input
                                value={mappingForm.security_group_dn}
                                onChange={(e) => setMappingForm({ ...mappingForm, security_group_dn: e.target.value })}
                                placeholder="CN=IT-Support,OU=Groups,DC=entreprise,DC=local"
                                data-testid="mapping-group-dn"
                            />
                        </div>
                        <div>
                            <Label className="form-label">Département cible *</Label>
                            <Select
                                value={mappingForm.department_id}
                                onValueChange={(v) => setMappingForm({ ...mappingForm, department_id: v })}
                            >
                                <SelectTrigger data-testid="mapping-department">
                                    <SelectValue placeholder="Sélectionner" />
                                </SelectTrigger>
                                <SelectContent>
                                    {departments.map((d) => (
                                        <SelectItem key={d.id} value={d.id}>
                                            {d.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="form-label">Priorité</Label>
                            <Input
                                type="number"
                                value={mappingForm.priority}
                                onChange={(e) => setMappingForm({ ...mappingForm, priority: parseInt(e.target.value) || 0 })}
                                data-testid="mapping-priority"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Plus la valeur est élevée, plus la priorité est haute
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            <Checkbox
                                checked={mappingForm.is_active}
                                onCheckedChange={(checked) => setMappingForm({ ...mappingForm, is_active: checked })}
                            />
                            <Label>Mapping actif</Label>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setMappingDialogOpen(false)}>
                            Annuler
                        </Button>
                        <Button
                            onClick={handleSaveMapping}
                            disabled={!mappingForm.security_group_dn || !mappingForm.security_group_name || !mappingForm.department_id}
                            data-testid="save-mapping-btn"
                        >
                            {editingMapping ? "Enregistrer" : "Créer"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Exclusion Dialog */}
            <Dialog open={exclusionDialogOpen} onOpenChange={setExclusionDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Nouvelle règle d'exclusion</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div>
                            <Label className="form-label">Utilisateur *</Label>
                            <Select
                                value={exclusionForm.user_id}
                                onValueChange={(v) => setExclusionForm({ ...exclusionForm, user_id: v })}
                            >
                                <SelectTrigger data-testid="exclusion-user">
                                    <SelectValue placeholder="Sélectionner" />
                                </SelectTrigger>
                                <SelectContent>
                                    {users.map((u) => (
                                        <SelectItem key={u.id} value={u.id}>
                                            {u.first_name} {u.last_name} ({u.email})
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="form-label">Security Group à ignorer *</Label>
                            <Select
                                value={exclusionForm.security_group_dn}
                                onValueChange={(v) => setExclusionForm({ ...exclusionForm, security_group_dn: v })}
                            >
                                <SelectTrigger data-testid="exclusion-group">
                                    <SelectValue placeholder="Sélectionner" />
                                </SelectTrigger>
                                <SelectContent>
                                    {mappings.map((m) => (
                                        <SelectItem key={m.id} value={m.security_group_dn}>
                                            {m.security_group_name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="form-label">Raison (optionnel)</Label>
                            <Textarea
                                value={exclusionForm.reason}
                                onChange={(e) => setExclusionForm({ ...exclusionForm, reason: e.target.value })}
                                placeholder="Pourquoi ignorer ce groupe pour cet utilisateur ?"
                                data-testid="exclusion-reason"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setExclusionDialogOpen(false)}>
                            Annuler
                        </Button>
                        <Button
                            onClick={handleSaveExclusion}
                            disabled={!exclusionForm.user_id || !exclusionForm.security_group_dn}
                            data-testid="save-exclusion-btn"
                        >
                            Créer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
